import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  IAuthContextData,
  ITenantsResponse,
  IUserKongData,
  ShopsGrantedCredentials,
  TenantData,
  User,
} from "../@types/Auth";
import { IPermissions } from "../@types/User";
import { decryptToken, encryptToken } from "../services/tokenConfig.service";
import usePermissions from "./api/Users/usePermissions";

import { api, wrapperRequests } from "../services/api";
import { routesURL } from "../services/routesUrl";
import { useTranslation } from "react-i18next";
import { TenantType } from "../@types/Stores";
import useLanguageSelector from "./useLanguageSelector";
import { isCountryAllowedToRefundAndCancellation } from "../shared/Countries";

const AuthContext = createContext({} as IAuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User>({} as User);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shopSelected, setShopSelected] = useState<ShopsGrantedCredentials>(
    {} as ShopsGrantedCredentials,
  );
  const [authError, setAuthError] = useState("");
  const { handleLoginLanguage } = useLanguageSelector();

  const { fetchUserPermissions } = usePermissions();

  const location = useLocation();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onIdle = () => {
    isLocked && !user.user ? signOut() : signOut(true);
  };

  const { getRemainingTime } = useIdleTimer({
    onIdle,
    crossTab: true,
    timeout:
      isLocked && !user.user
        ? Number(process.env.REACT_APP_LOGIN_TIMEOUT)
        : Number(process.env.REACT_APP_PIN_TIMEOUT),
    throttle: 500,
  });

  function verifyDefaultShop(defaultShop: ShopsGrantedCredentials) {
    if (!defaultShop || defaultShop.id === "") {
      throw new Error("without_store_assigned");
    }
  }

  const showToast = (message: string, type: "success" | "error") => {
    toast[type](message, {
      position: "top-center",
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  async function fetchTenantData(
    tenantName: string,
  ): Promise<TenantData | null> {
    try {
      const response = await wrapperRequests<ITenantsResponse>(
        routesURL.settings.stores.getAllTenants,
        "GET",
      );

      const tenant = response.data?.tenants?.find(
        (t: TenantType) => t.name === tenantName,
      );

      if (tenant) {
        return {
          id: tenant.id,
          name: tenant.name,
          currency: tenant.currency,
          dateFormat: tenant.dateFormat,
          countryCode: tenant.countryCode,
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching tenant data:", error);
      return null;
    }
  }

  async function pinSignIn(pin: string) {
    setIsLoading(true);
    setAuthError("");

    const shopStoraged = localStorage.getItem("@Incycle:shopSelected");
    const parsedShopStoraged = shopStoraged ? JSON.parse(shopStoraged) : "";
    const managerId = localStorage.getItem("@Incycle:manager");

    if (!managerId) {
      logout();
      showToast("Manager not found, please sign in again.", "error");
      setIsLoading(false);
      return;
    }

    try {
      const response = await wrapperRequests<IUserKongData>(
        routesURL.auth.pinSignInKong,
        "POST",
        {
          data: {
            pin: pin,
            shopCode: parsedShopStoraged.code,
            tenantName: parsedShopStoraged.tenantName,
            consumerUsername: "centralizer_website",
            userId: managerId,
          },
        },
      );

      verifyDefaultShop(response.data?.user?.defaultShop);
      handleLoginLanguage(response.data.user.language);

      api.defaults.headers.common["Authorization"] =
        `${response.data?.accessToken}`;

      const tenantData = await fetchTenantData(
        response.data.user.defaultShop.tenantName,
      );

      const permissionsResponse = await fetchUserPermissions(
        response.data.user.id,
      );

      const onlyTruePermissions =
        permissionsResponse &&
        permissionsResponse
          .filter(
            (permission: IPermissions) =>
              permission.hasPermission && permission.environment === "WEB",
          )
          .map((permission: IPermissions) => permission.permissionString);

      const userData: User = {
        ...response.data,
        user: {
          ...response.data.user,
          permissions: onlyTruePermissions,
        },
      };

      const encryptedUserData = encryptToken(JSON.stringify(userData));

      localStorage.setItem("@Incycle:user", encryptedUserData);

      setUser(userData);

      const shopWithTenant = {
        ...parsedShopStoraged,
        tenant: tenantData || undefined,
      };

      setShopSelected(shopWithTenant);
      localStorage.setItem(
        "@Incycle:shopSelected",
        JSON.stringify(shopWithTenant),
      );

      setShopSelected(shopWithTenant);
      localStorage.setItem(
        "@Incycle:shopSelected",
        JSON.stringify(shopWithTenant),
      );

      showToast(`🚴‍♂️ ${t("welcome")}, ${response.data.user.name}!`, "success");

      setAuthError("");
      setIsLoading(false);
      navigate("/");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setIsLoading(false);
      setIsLoading(false);
      if (err?.message === "without_store_assigned") {
        showToast("User without a default store assigned.", "error");
      }
      setAuthError(err?.response?.data?.message);
    }
  }

  const signIn = useCallback(async ({ email, password, countryCode }) => {
    setIsLoading(true);
    try {
      const response = await wrapperRequests<IUserKongData>(
        routesURL.auth.signInKong,
        "POST",
        {
          data: {
            email,
            password,
            countryCode,
            consumerUsername: "centralizer_website",
          },
        },
      );

      verifyDefaultShop(response.data?.user?.defaultShop);
      handleLoginLanguage(response.data.user.language);

      localStorage.setItem("@Incycle:manager", response.data.user.id);

      api.defaults.headers.common["Authorization"] =
        ` ${response.data?.accessToken}`;

      const tenantData = await fetchTenantData(
        response.data.user.defaultShop.tenantName,
      );

      const shopWithTenant = {
        ...response.data.user.defaultShop,
        tenant: tenantData || undefined,
      };

      setShopSelected(shopWithTenant);

      localStorage.setItem(
        "@Incycle:shopSelected",
        JSON.stringify(shopWithTenant),
      );

      const permissionsResponse = await fetchUserPermissions(
        response.data.user.id,
      );

      const onlyTruePermissions =
        permissionsResponse &&
        permissionsResponse
          .filter(
            (permission: IPermissions) =>
              permission.hasPermission && permission.environment === "WEB",
          )
          .map((permission: IPermissions) => permission.permissionString);

      const userData: User = {
        ...response.data,
        user: {
          ...response.data.user,
          permissions: onlyTruePermissions,
        },
      };

      const encryptedUserData = encryptToken(JSON.stringify(userData));

      localStorage.setItem("@Incycle:user", encryptedUserData);

      setUser(userData);

      showToast(`🚴‍♂️ ${t("welcome")}, ${response.data.user.name}!`, "success");

      navigate("/");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false);
      if (error?.message === "without_store_assigned") {
        showToast("User without a default store assigned.", "error");
      } else {
        showToast("Login error, check your data!", "error");
        throw Error(String(error));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectStore = async (store: ShopsGrantedCredentials) => {
    try {
      const tenantData = await fetchTenantData(store.tenantName);

      const shopWithTenant = {
        ...store,
        tenant: tenantData || undefined,
      };

      setShopSelected(shopWithTenant);
      localStorage.setItem(
        "@Incycle:shopSelected",
        JSON.stringify(shopWithTenant),
      );
    } catch (error) {
      console.error("Error selecting store:", error);
      setShopSelected(store);
      localStorage.setItem("@Incycle:shopSelected", JSON.stringify(store));
    }
  };

  const signOut = useCallback(async (lock?: boolean) => {
    setAuthError("");

    user?.user &&
      (await wrapperRequests(routesURL.auth.singOut, "POST", {
        data: {
          accessToken: user?.accessToken,
          userId: user?.user?.id,
        },
      }));

    localStorage.removeItem("@Incycle:user");

    const shopStoraged = localStorage.getItem("@Incycle:shopSelected");

    delete api.defaults.headers.common.Authorization;

    if (lock && shopStoraged) {
      localStorage.setItem("@Incycle:isLocked", JSON.stringify(true));
      setUser({} as User);
      setIsLocked(true);
      navigate("/pinSignIn");
    } else {
      localStorage.removeItem("@Incycle:shopSelected");
      localStorage.removeItem("@Incycle:isLocked");
      localStorage.removeItem("@Incycle:manager");
      setUser({} as User);
      setIsLocked(false);
      navigate("/signin");
    }
  }, []);

  function logout() {
    localStorage.removeItem("@Incycle:shopSelected");
    localStorage.removeItem("@Incycle:isLocked");
    localStorage.removeItem("@Incycle:manager");

    setUser({} as User);
    setIsLocked(false);
    navigate("/signin");
  }

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!user?.user?.permissions) {
        return false;
      }
      return user.user.permissions.includes(permission);
    },
    [user?.user?.permissions],
  );

  useEffect(() => {
    async function loadTokenStorage(): Promise<void> {
      setInitialLoading(true);
      const shopStoraged = localStorage.getItem("@Incycle:shopSelected");
      const isLockedStoraged = localStorage.getItem("@Incycle:isLocked");
      const userDataStoraged = localStorage.getItem("@Incycle:user");

      getRemainingTime();

      try {
        const userDataDecrypted = decryptToken(userDataStoraged || "");
        const userDataParsed = userDataDecrypted
          ? JSON.parse(userDataDecrypted)
          : "";
        const userData: User = userDataParsed ? JSON.parse(userDataParsed) : "";

        const parsedShopStoraged = shopStoraged ? JSON.parse(shopStoraged) : "";
        const parsedIsLockedStoraged = isLockedStoraged
          ? JSON.parse(isLockedStoraged)
          : false;

        setIsLocked(parsedIsLockedStoraged);

        if (
          userData.accessToken &&
          userData.refreshToken &&
          parsedShopStoraged
        ) {
          api.defaults.headers.common["Authorization"] =
            `${userData.accessToken}`;

          await wrapperRequests(routesURL.auth.verifyToken, "POST");

          const shopToSet = parsedShopStoraged || userData?.user?.defaultShop;

          setUser({
            ...userData,
            user: {
              ...userData.user,
              permissions: userData.user.permissions,
            },
          });

          setShopSelected(shopToSet);

          navigate(`${location.pathname}${location.search && location.search}`);
        }
      } catch (error) {
        console.log(error);
        signOut();
      } finally {
        setInitialLoading(false);
      }
    }

    loadTokenStorage();
  }, []);

  const countryCode = localStorage.getItem("countryCode");
  const isUnitedStates = countryCode?.toLowerCase() === "us";
  const canRefundAndCancelByCountry =
    isCountryAllowedToRefundAndCancellation(countryCode);

  return (
    <AuthContext.Provider
      value={{
        authError,
        isLoading,
        initialLoading,
        isLocked,
        logout,
        pinSignIn,
        shopSelected,
        selectStore,
        setShopSelected,
        signIn,
        signOut,
        user,
        hasPermission,
        isUnitedStates,
        canRefundAndCancelByCountry,
        fetchTenantData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider.");
  }

  return context;
}

export { AuthProvider, useAuth };
